// =======================
// detalhesEscola.js
// =======================

import React, { useState, useEffect } from "react";
import {
  CssBaseline,
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Tab,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import SchoolIcon from "@mui/icons-material/School";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import ListaAtos from "../../../componentes/ListaAtos";
import { botaoVoltar } from "../../../estilos/botoes";
import { api } from "@api/api";
import { ajustarTamanhoFonteAnalista } from "../../../estilos/global";

const Field = ({ label, value, borderRight = false, sx = {} }) => (
  <Box
    sx={{
      px: 2,
      py: 1.5,
      minWidth: 0,
      borderRight: {
        xs: "none",
        md: borderRight ? "1px solid #e0e0e0" : "none",
      },
      ...sx,
    }}
  >
    <Typography
      sx={{
        color: "text.secondary",
        fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>

    <Typography
      sx={{
        color: "text.primary",
        fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
        lineHeight: 1.35,
        mt: 0.5,
        overflowWrap: "anywhere",
      }}
    >
      {value || "Não informado"}
    </Typography>
  </Box>
);

const InfoCard = ({ icon, title, children }) => (
  <Box
    sx={{
      p: 2,
      minWidth: 0,
      height: "100%",
      boxSizing: "border-box",
      border: "1px solid #c9def4",
      borderRadius: "6px",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.75,
        color: "primary.main",
        "& .MuiSvgIcon-root": {
          fontSize: "1.35rem",
        },
      }}
    >
      {icon}

      <Typography
        sx={{
          color: "primary.main",
          fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
          fontWeight: 600,
          lineHeight: 1.2,
        }}
      >
        {title}
      </Typography>
    </Box>

    {children}
  </Box>
);

const formatCNPJ = (cnpj) => {
  if (!cnpj) return "Não informado";

  const numeros = String(cnpj).replace(/\D/g, "");

  if (numeros.length !== 14) return cnpj;

  return numeros.replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    "$1.$2.$3/$4-$5",
  );
};

export default function InfoEscola() {
  const [tabValue, setTabValue] = useState("1");
  const [escola, setEscola] = useState(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    async function fetchEscola() {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(api.schoolDetails(id), {
          credentials: "include",
        });

        if (response.status === 404) {
          setEscola(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Erro ao buscar escola: ${response.status}`);
        }

        const data = await response.json();
        setEscola(data);
      } catch (error) {
        console.error("Erro ao buscar escola:", error);
        setEscola(null);
        setError("Não foi possível carregar os dados da escola.");
      } finally {
        setLoading(false);
      }
    }

    fetchEscola();
  }, [id, retryAttempt]);

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <Container maxWidth={false} disableGutters sx={{ marginTop: 4 }}>
        <Typography variant="h6">Carregando...</Typography>
      </Container>
    );
  }

  // Falha da API ou conexão
  if (error) {
    return (
      <Container maxWidth={false} disableGutters sx={{ pt: 3, pb: 4 }}>
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setRetryAttempt((valor) => valor + 1)}
                sx={{
                  fontWeight: 600,
                  textTransform: "none",
                  borderRadius: "6px",
                }}
              >
                Tentar novamente
              </Button>

              <Button
                variant="outlined"
                onClick={handleBackClick}
                startIcon={<ArrowBackRoundedIcon />}
                sx={[botaoVoltar, { mb: 1.5 }]}
              >
                Voltar
              </Button>
            </Box>
          }
          sx={{
            alignItems: "center",
            "& .MuiAlert-message": {
              flex: 1,
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
              mr: 0,
            },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Não foi possível carregar os dados da escola.
          </Typography>

          <Typography variant="body2">
            Verifique sua conexão e tente novamente.
          </Typography>
        </Alert>
      </Container>
    );
  }

  // Escola inexistente
  if (!escola) {
    return (
      <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
        <Alert
          severity="warning"
          variant="outlined"
          action={
            <Button
              variant="outlined"
              onClick={handleBackClick}
              startIcon={<ArrowBackRoundedIcon />}
              sx={[botaoVoltar, { mb: 1.5 }]}
            >
              Voltar
            </Button>
          }
          sx={{
            alignItems: "center",
            "& .MuiAlert-message": {
              flex: 1,
            },
            "& .MuiAlert-action": {
              alignItems: "center",
              pt: 0,
              mr: 0,
            },
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            Escola não encontrada.
          </Typography>

          <Typography variant="body2">
            O registro informado não existe ou não está mais disponível.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <CssBaseline />

      <Container maxWidth={false} disableGutters sx={{ py: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            mb: 0.5,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBackClick}
            startIcon={<ArrowBackRoundedIcon />}
            sx={[botaoVoltar, { mb: 1.5 }]}
          >
            Voltar
          </Button>

          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "primary.main",
              fontSize: { xs: ajustarTamanhoFonteAnalista("1.65rem"), sm: ajustarTamanhoFonteAnalista("1.75rem") },
              textAlign: "left",
            }}
          >
            Perfil Escolar
          </Typography>
        </Box>

        <TabContext value={tabValue}>
          <Box sx={{ display: "flex", alignItems: "flex-end" }}>
            <TabList
              onChange={handleChangeTab}
              aria-label="Abas do perfil escolar"
              sx={{
                minHeight: 44,
                "& .MuiTabs-indicator": {
                  display: "none",
                },
                "& .MuiTabs-flexContainer": {
                  gap: 0.5,
                },
              }}
            >
              <Tab
                label="Informações da Escola"
                value="1"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />

              <Tab
                label="Atos da Escola"
                value="2"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />

              <Tab
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box component="span">Mantenedoras Vinculadas</Box>

                    <Box
                      component="span"
                      sx={{
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        backgroundColor: tabValue === "3" ? "#fff" : "#eaf3fd",
                        color: "primary.main",
                        fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                        fontWeight: 700,
                      }}
                    >
                      {escola.mantenedoras?.length || 0}
                    </Box>
                  </Box>
                }
                value="3"
                sx={{
                  minHeight: 44,
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  fontSize: { xs: ajustarTamanhoFonteAnalista("0.85rem"), sm: ajustarTamanhoFonteAnalista("1rem") },
                  fontWeight: 500,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "primary.main",
                  borderBottom: 0,
                  borderRadius: "6px 6px 0 0",
                  color: "text.primary",
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                    color: "#fff",
                  },
                }}
              />
            </TabList>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "0 8px 8px 8px",
            }}
          >
            <TabPanel value="1" sx={{ p: 0 }}>
              <Box
                sx={{
                  p: 2,
                  mb: 1.5,
                  border: "1px solid #c9def4",
                  borderRadius: "6px",
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    color: "primary.main",
                    fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                    fontWeight: 600,
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  Nome da Escola
                </Typography>

                <Typography
                  sx={{
                    color: "text.primary",
                    fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                    fontWeight: 500,
                    lineHeight: 1.35,
                  }}
                >
                  {escola.nome || "Não informado"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1.2fr 0.82fr 1fr",
                  },
                  gap: 1.5,
                  alignItems: "stretch",
                }}
              >
                <InfoCard icon={<LocationOnIcon />} title="Endereço">
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns:
                        "minmax(0, 1.4fr) minmax(80px, 0.8fr)",
                    }}
                  >
                    <Field
                      label="Logradouro (Rua/Avenida)"
                      value={escola.rua}
                      sx={{
                        px: 0,
                        py: 1,
                        pr: 1.5,
                        borderRight: "1px solid #d8e3ef",
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Número"
                      value={escola.numero}
                      sx={{
                        px: 0,
                        py: 1,
                        pl: 1.5,
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="Bairro"
                      value={escola.bairro}
                      sx={{
                        px: 0,
                        py: 1,
                        pr: 1.5,
                        borderRight: "1px solid #d8e3ef",
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />

                    <Field
                      label="CEP"
                      value={escola.cep}
                      sx={{
                        px: 0,
                        py: 1,
                        pl: 1.5,
                        borderBottom: "1px solid #d8e3ef",
                      }}
                    />
                  </Box>

                  <Field
                    label="Município"
                    value={escola.municipio}
                    sx={{
                      px: 0,
                      pt: 1.25,
                      pb: 0,
                    }}
                  />
                </InfoCard>

                <InfoCard icon={<PhoneIcon />} title="Contatos">
                  <Field
                    label="Telefone"
                    value={escola.contato}
                    sx={{
                      px: 0,
                      py: 1,
                      borderBottom: "1px solid #d8e3ef",
                    }}
                  />

                  <Field
                    label="E-mail"
                    value={escola.email}
                    sx={{
                      px: 0,
                      pt: 1.25,
                      pb: 0,
                    }}
                  />
                </InfoCard>

                <InfoCard icon={<SchoolIcon />} title="Etapas de ensino">
                  <Typography
                    sx={{
                      color: "text.secondary",
                      fontSize: ajustarTamanhoFonteAnalista("0.75rem"),
                      lineHeight: 1.2,
                      mt: 1,
                    }}
                  >
                    Modalidades de ensino
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.75,
                      mt: 0.75,
                    }}
                  >
                    {escola.modalidades && escola.modalidades.length > 0 ? (
                      escola.modalidades.map((modalidade) => (
                        <Box
                          key={modalidade}
                          sx={{
                            px: 1.25,
                            py: 0.4,
                            borderRadius: "999px",
                            backgroundColor: "#eaf3fd",
                            color: "#075fc8",
                            fontSize: ajustarTamanhoFonteAnalista("0.8rem"),
                            fontWeight: 600,
                          }}
                        >
                          {modalidade}
                        </Box>
                      ))
                    ) : (
                      <Typography
                        sx={{
                          color: "text.primary",
                          fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                        }}
                      >
                        Nenhuma modalidade registrada.
                      </Typography>
                    )}
                  </Box>
                </InfoCard>
              </Box>
            </TabPanel>{" "}
            <TabPanel value="2" sx={{ p: 0 }}>
              <ListaAtos />
            </TabPanel>
            <TabPanel value="3" sx={{ p: 0 }}>
              {escola.mantenedoras?.length > 0 ? (
                <Box sx={{ display: "grid", gap: 1.25 }}>
                  {escola.mantenedoras.map((mantenedora) => (
                    <Box
                      key={mantenedora.id}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          md: "minmax(0, 1.5fr) minmax(130px, 0.7fr) minmax(0, 1fr) auto",
                        },
                        alignItems: "center",
                        gap: { xs: 1.5, md: 2 },
                        p: 1.5,
                        border: "1px solid #c9def4",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                      }}
                    >
                      {/* Nome e CNPJ */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.25,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor: "#e7f1fd",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <BusinessIcon
                            sx={{
                              color: "primary.main",
                              fontSize: 20,
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: "primary.main",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            Nome da Mantenedora
                          </Typography>

                          <Typography
                            sx={{
                              color: "text.primary",
                              fontSize: ajustarTamanhoFonteAnalista("1.1rem"),
                              fontWeight: 500,
                              lineHeight: 1.35,
                              mt: 0.5,
                              overflowWrap: "anywhere",
                            }}
                          >
                            {mantenedora.nome || "Não informado"}
                          </Typography>

                          <Box
                            sx={{
                              display: "inline-flex",
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: "999px",
                              backgroundColor: "#eaf3fd",
                              color: "#075fc8",
                              fontSize: ajustarTamanhoFonteAnalista("0.8rem"),
                              fontWeight: 600,
                            }}
                          >
                            CNPJ {formatCNPJ(mantenedora.cnpj)}
                          </Box>
                        </Box>
                      </Box>

                      {/* Cidade */}
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "primary.main",
                          }}
                        >
                          <LocationOnIcon sx={{ fontSize: "1.35rem" }} />

                          <Typography
                            sx={{
                              color: "primary.main",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            Cidade
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            color: "text.primary",
                            fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                            fontWeight: 400,
                            lineHeight: 1.35,
                            mt: 0.5,
                          }}
                        >
                          {mantenedora.cidade || "Não informado"}
                        </Typography>
                      </Box>

                      {/* E-mail */}
                      <Box sx={{ minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "primary.main",
                          }}
                        >
                          <EmailIcon sx={{ fontSize: "1.35rem" }} />

                          <Typography
                            sx={{
                              color: "primary.main",
                              fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                              fontWeight: 600,
                              lineHeight: 1.2,
                            }}
                          >
                            E-mail
                          </Typography>
                        </Box>

                        <Typography
                          sx={{
                            color: "text.primary",
                            fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                            fontWeight: 400,
                            lineHeight: 1.35,
                            mt: 0.5,
                            overflowWrap: "anywhere",
                          }}
                        >
                          {mantenedora.email || "Não informado"}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        size="small"
                        onClick={() =>
                          navigate(`/perfil-mantenedora/${mantenedora.id}`)
                        }
                        sx={{
                          minWidth: 100,
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "6px",
                        }}
                      >
                        Ver perfil
                      </Button>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: ajustarTamanhoFonteAnalista("0.95rem"),
                  }}
                >
                  Nenhuma mantenedora vinculada foi encontrada.
                </Typography>
              )}
            </TabPanel>
          </Paper>
        </TabContext>
      </Container>
    </motion.div>
  );
}
